import React, { ReactNode, useEffect, useState } from 'react'
import {
    CompaniesYouWillLove,
    Hooks,
    JobYouWillLove,
    Toast,
    Utils,
    useIdentify
} from '@microfrontends/react-components'
import {
    ELanguage,
    EMode,
    Footer,
    Header,
    TUser,
    initialDataProvider,
    EPages,
    getSeoKeywordAPI,
    TSeoKeywordData
} from '@microfrontends/app-shell-v2'
import dynamic from 'next/dynamic'

import { Body, ChildrenContainer, Container, LeftContainer, RightContainer } from './MainLayout.css'
import PodcastAudio from '../../PodcastAudio'
import MetaTags from '../MetaTags'

// not SSR because initial value isShowMenu not working on first render
const SideNavigation = dynamic(() => import('./SideNavigation'), { ssr: false })
import { Notifications } from '@microfrontends/react-components'

import { EDevice, useStore } from '../../../store'
import userService, { TAuthUser } from '../../../services/userService'
import {
    ACTION_SET_BREAKPOINT,
    ACTION_SET_DEVICE,
    ACTION_SET_IS_MOBILE,
    ACTION_SET_PROFILE_LEVEL,
    ACTION_SET_USER
} from '../../../store/actions'
import { getCookie } from '../../../helpers/cookies'
import { useRouter } from 'next/router'
import { calculateProfileStrength } from '../../../helpers/util'
import profileService from '../../../services/profileService'
import { CAREER_CENTER_SHOW_MENU, firebaseConfig, tabSection } from './constant'
import { TNotificationProps } from './type'
import { strengthOption } from '../../ProfileStrength/const'

const BASE_PATH = process.env.NEXT_PUBLIC_MS_BASE_PATH
export const ENABLE_COMPANY_INDUSTRY = !!Number(process.env.ENABLE_COMPANY_INDUSTRY)
export const ENABLE_JOB_FUNCTION = !!Number(process.env.ENABLE_JOB_FUNCTION)
const msBaseUrl = process.env.NEXT_PUBLIC_MICROSERVICE_DOMAIN ?? ''

type TMainLayoutProps = {
    children?: ReactNode
    pageName: EPages
    responsiveCornerColor?: string
    userIp: string
}

const MainLayout = (props: TMainLayoutProps) => {
    const { children, pageName, responsiveCornerColor = '#fff', userIp } = props
    const { state, dispatch } = useStore()
    const { user, langCode, device, t, profileLevel } = state
    const router = useRouter()
    useIdentify(user, msBaseUrl)

    const [isRerenderHeader, setReRenderHeader] = useState<boolean>(false)
    const [isShowMenu, setShowMenu] = useState(true)
    const [seoKeywords, setSeoKeywords] = useState<TSeoKeywordData[]>([])

    const isShowReferral = process.env.ENABLE_REFERRAL !== undefined ? process.env.ENABLE_REFERRAL === '1' : false
    const loginUrl = `${process.env.NEXT_PUBLIC_VNW_JOBSEEKER_URL}/${langCode === ELanguage.EN ? 'login' : 'dang-nhap'}`

    const percentage = calculateProfileStrength(profileLevel, strengthOption)

    const [breakpoint] = Hooks.useResize()
    const isShowNotification = Hooks.useInitNotification()

    const notificationProps = ({
        langCode: langCode,
        firebaseConfig: firebaseConfig,
        baseMsUrl: process.env.NEXT_PUBLIC_MS_BASE_PATH,
        jobSeekerUrl: process.env.NEXT_PUBLIC_VNW_JOBSEEKER_URL,
        user: user
    } as TNotificationProps) as any

    const isHaveRightContainer = () => {
        if (device !== EDevice.DESKTOP) {
            return false
        }
        if (router.pathname.includes(t('/my-career-center/dashboard'))) {
            return false
        }

        return true
    }

    const getLevel = async () => {
        const level = await profileService.getLevel()
        if (level) {
            dispatch({
                type: ACTION_SET_PROFILE_LEVEL,
                payload: {
                    ...profileLevel,
                    level: level.resumeLevelId,
                    passedPoints: level.currentPoint,
                    totalPoints: level.totalPoint,
                    status: level.resumeStatus,
                    profileStrength: level.profileStrength ?? 0
                }
            })
        }
    }

    const updateUserState = (user: TAuthUser) => {
        const setUserAndUserRelatedData = (user: TAuthUser) => {
            dispatch({
                type: ACTION_SET_USER,
                payload: { ...user, userIp }
            })
        }

        setUserAndUserRelatedData(user)
    }

    const getTokenAndDispatchUser = async () => {
        let jsToken = ''
        let wsToken = ''

        // get Token
        if (window) {
            jsToken = decodeURIComponent(getCookie('VNW128450527232960') || '')
            wsToken = decodeURIComponent(getCookie('VNWWS128450527232960') || '')

            if (jsToken === '' || wsToken === '') {
                window.location.href = `${loginUrl}?type=login&redirectURL=${encodeURIComponent(window.location.href)}`
                return
            }
        }

        // get user info and dispatch
        const user: TAuthUser | null = await userService.authenticate(jsToken, wsToken)
        if (user) {
            updateUserState(user)
        } else {
            const redirectToLogin = () => {
                if (typeof window !== 'undefined') {
                    window.location.href = `${loginUrl}?type=login&redirectURL=${encodeURIComponent(
                        window.location.href
                    )}`
                    return
                }
            }
            redirectToLogin()
        }
    }

    const initSelectData = async () => {
        await initialDataProvider.initSelectData({
            selectDataKey: ['industry', 'jobFunction', 'level'],
            mode: process.env.APP_MODE as any,
            langCode
        })
    }

    const handleShowMenu = (isShow: boolean) => {
        setShowMenu(isShow)
        localStorage.setItem(CAREER_CENTER_SHOW_MENU, JSON.stringify(isShow))
    }

    const getToastMessageFromCookie = () => {
        const showToastMessage = Utils.getCookie('VNW_SHOW_TOAST')
        // reset cookie
        Utils.setCookie('VNW_SHOW_TOAST', '', -1)
        if (!showToastMessage) {
            return
        }
        Utils.showToast(t('Success'), showToastMessage)
    }

    const getSeoKeyword = async () => {
        const data = await getSeoKeywordAPI(process.env.NEXT_PUBLIC_MS_BASE_PATH || '')
        if (data && data.length > 0) {
            setSeoKeywords(data)
        }
    }

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const initShow = JSON.parse(localStorage.getItem(CAREER_CENTER_SHOW_MENU) || 'true')
            setShowMenu(initShow)
        }
        // Authenticate
        getTokenAndDispatchUser()

        // Set current state to mobile/tablet
        dispatch({
            type: ACTION_SET_IS_MOBILE,
            payload: Utils.isPhone() || Utils.isTablet()
        })
        setReRenderHeader(true)

        // get profile strength
        getLevel()

        // get seo keyword
        getSeoKeyword()

        // handle device change
        const handleDeviceChange = () => {
            if (window.innerWidth <= 768) {
                dispatch({
                    type: ACTION_SET_DEVICE,
                    payload: EDevice.MOBILE
                })
            } else if (window.innerWidth > 768 && window.innerWidth <= 1024) {
                dispatch({
                    type: ACTION_SET_DEVICE,
                    payload: EDevice.TABLET
                })
            } else if (window.innerWidth > 1024 && window.innerWidth <= 1440) {
                dispatch({
                    type: ACTION_SET_DEVICE,
                    payload: EDevice.LAPTOP
                })
            } else {
                dispatch({
                    type: ACTION_SET_DEVICE,
                    payload: EDevice.DESKTOP
                })
            }
        }

        handleDeviceChange()

        initSelectData()

        getToastMessageFromCookie()

        window.addEventListener('resize', handleDeviceChange)
        window.addEventListener('UPDATE_USER', () => location.reload())

        return () => {
            window.removeEventListener('resize', handleDeviceChange)
            window.removeEventListener('UPDATE_USER', () => location.reload())
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        dispatch({
            type: ACTION_SET_BREAKPOINT,
            payload: breakpoint
        })
    }, [dispatch, breakpoint])

    const userInfo = { ...user, jobLevelId: user?.jobLevelId[0], id: user?.id ?? 0 }
    const param = router.query.section || ''
    const section = pageName === EPages.MY_JOBS && param === '' ? 'applied' : param

    return (
        <Container>
            <MetaTags />
            <Toast />

            <Header
                mode={process.env.APP_MODE as EMode}
                langCode={langCode}
                user={(user as unknown) as TUser}
                pageName={pageName}
                isHaveBottomTab={false}
                subMenuSection={tabSection[section as any]}
                responsive={{
                    type: 'sub-page',
                    pageTitle: t('Career center'),
                    cornerColor: responsiveCornerColor,
                    backLink: t('/account-center')
                }}
            />

            <Body device={device}>
                <LeftContainer device={device} isShowMenu={isShowMenu}>
                    <SideNavigation
                        langCode={langCode}
                        device={device}
                        user={user}
                        percentProfile={percentage}
                        isShow={isShowMenu}
                        onShowChange={(e: boolean) => handleShowMenu(e)}
                    />
                </LeftContainer>

                <ChildrenContainer
                    device={device}
                    isShowMenu={isShowMenu}
                    isHaveRightContainer={isHaveRightContainer()}
                >
                    {children}
                </ChildrenContainer>

                {isHaveRightContainer() && (
                    <RightContainer device={device} isShowMenu={isShowMenu}>
                        {user && (
                            <JobYouWillLove
                                isMobile={device === EDevice.MOBILE}
                                api={{
                                    baseUrl: process.env.NEXT_PUBLIC_MS_BASE_PATH || '',
                                    accessToken: user?.accessToken,
                                    query: {
                                        limit: '10',
                                        user: String(user?.userId)
                                    },
                                    appId: process.env.VNW_APP_ID || '',
                                    source: 'Career Center - Jobs You Will Love'
                                }}
                                utmType='my-profile'
                                langCode={langCode}
                                previewJob={{
                                    enable: device !== EDevice.MOBILE,
                                    baseUrl: process.env.NEXT_PUBLIC_VNW_JOBSEEKER_URL || ''
                                }}
                                classSelector='myjobs_recommend view_job_item'
                                userInfo={userInfo}
                                initData={{
                                    locations: initialDataProvider.getLocations(),
                                    jobFunctions: initialDataProvider.getJobFunction(),
                                    companyIndustries: initialDataProvider.getIndustries(),
                                    jobLevels: initialDataProvider.getLevels(),
                                    educations: initialDataProvider.getHighestDegrees(),
                                    countries: initialDataProvider.getCountries(),
                                    cities: initialDataProvider.getLocations(),
                                    districts: initialDataProvider.getDistricts(),
                                    benefits: initialDataProvider.getBenefits()
                                }}
                            />
                        )}

                        <div style={{ marginTop: 8 }}>
                            <CompaniesYouWillLove
                                langCode={langCode}
                                accessToken={user?.accessToken || ''}
                                msUrl={BASE_PATH || ''}
                                userId={user?.userId || 0}
                                options={{
                                    isSideView: true,
                                    maxItemOnRow: 1
                                }}
                            />
                        </div>

                        <PodcastAudio language={langCode} />
                    </RightContainer>
                )}
            </Body>

            {isShowNotification && user && <Notifications config={notificationProps} user={user} langCode={langCode} />}
            <Footer
                mode={process.env.APP_MODE as EMode}
                langCode={langCode}
                user={user as any}
                seoKeywordData={seoKeywords}
            />
        </Container>
    )
}

export default MainLayout
